import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { X, Search, Globe, BookOpen, ChevronDown, MapPin, Flame, Share2, Check, Film, Heart } from "lucide-react";
import { getIcon } from "./iconMap";
import { logoFor, brandDomain } from "@/lib/brandLogos";
import { feedCapacitor } from "@/lib/capacitor";
import { createElement } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MiniBtn = ({ testId, onClick, disabled, title, tone, label, children }) => (
  <span className="flex flex-col items-center gap-0.5 shrink-0">
    <button
      data-testid={testId}
      title={title}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); if (!disabled) onClick(); }}
      className={`w-9 h-9 rounded-xl flex items-center justify-center border text-base shrink-0 transition-transform ${
        disabled ? "opacity-25 cursor-not-allowed bg-slate-800/60 border-slate-700/60" : `${tone} active:scale-90`
      }`}
    >
      {children}
    </button>
    {label && <span className={`text-[8px] font-black tracking-wide ${disabled ? "text-slate-600" : "text-slate-400"}`}>{label}</span>}
  </span>
);

const MerchantRow = ({ b, marked, shared, onToggleMark, onShare, onMerchant, onRoute, openLink, appMap }) => {
  const links = b.links || {};
  const isAndroid = /android/i.test(navigator.userAgent);
  const finderApp = appMap ? appMap[(b.name || "").trim().toLowerCase()] : null;
  const appLink = (isAndroid ? (links.android_app || links.ios_app) : (links.ios_app || links.android_app)) || (finderApp && finderApp.app_url) || "";
  const pogLink = links.pog_link || links.order_url;
  const inferredDom = brandDomain(b.name || "");
  const webLink = links.website || pogLink || (inferredDom ? `https://${inferredDom}` : "");
  const dealText = b.featured_deal || (b.has_deals ? "Live deal — tap to see it!" : null);
  return (
    <div
      data-testid={`pogodex-merchant-${b.id}`}
      role="button"
      tabIndex={0}
      onClick={() => onMerchant(b)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onMerchant(b); } }}
      className="w-full flex flex-wrap items-center gap-2.5 px-3 py-2.5 rounded-2xl hover:bg-slate-800/60 transition-colors text-left cursor-pointer"
    >
      <span className="relative w-10 h-10 rounded-xl bg-white/95 shadow overflow-hidden shrink-0 flex items-center justify-center">
        <span className="font-heading font-black text-sm text-slate-700">{(b.name || "?").charAt(0).toUpperCase()}</span>
        {logoFor(b, null, BACKEND_URL) && (
          <img src={logoFor(b, null, BACKEND_URL)} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-contain p-1 bg-white" onError={(e) => { e.currentTarget.style.display = "none"; }}
            onLoad={(e) => { if (e.currentTarget.naturalWidth > 0 && e.currentTarget.naturalWidth < 40) e.currentTarget.className = "absolute inset-0 m-auto w-6 h-6 object-contain bg-white"; }} />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="font-heading font-bold text-sm text-white truncate">{b.name}</span>
          {b.is_new && <span className="text-[9px] font-black text-emerald-300 bg-emerald-500/20 border border-emerald-400/40 rounded-full px-1.5 py-0.5 shrink-0" data-testid={`pogodex-new-${b.id}`}>🆕 NEW</span>}
          {b.featured && <span className="text-[9px] font-black text-amber-300 bg-amber-500/20 border border-amber-400/40 rounded-full px-1.5 py-0.5 shrink-0">⭐ FEATURED</span>}
        </span>
        <span className={`block text-[11px] font-bold truncate ${dealText ? "text-amber-300" : "text-slate-500"}`}>
          {dealText ? `🔥 ${dealText}` : "No deals yet"}
          {b.distance_miles != null && <span className="text-slate-500 font-semibold"> · {b.distance_miles} mi</span>}
        </span>
      </span>
      <span className="w-full flex items-center gap-1.5 pl-1">
        <MiniBtn testId={`pogodex-share-${b.id}`} title="Share this spot" label="Share" tone="bg-cyan-500/15 border-cyan-400/40" onClick={() => onShare(b)}>
          {shared ? <Check className="w-4 h-4 text-emerald-300" /> : <Share2 className="w-4 h-4 text-cyan-300" />}
        </MiniBtn>
        <MiniBtn testId={`pogodex-app-${b.id}`} title="Open their app" label="App" disabled={!appLink} tone="bg-orange-500/15 border-orange-400/50" onClick={() => openLink(b.id, "app", appLink)}>
          <Flame className="w-4 h-4 text-orange-300 fill-orange-400/60" />
        </MiniBtn>
        <MiniBtn testId={`pogodex-web-${b.id}`} title="Website" label="Web" disabled={!webLink} tone="bg-indigo-500/15 border-indigo-400/40" onClick={() => openLink(b.id, "website", webLink)}>
          <Globe className="w-4 h-4 text-indigo-300" />
        </MiniBtn>
        <MiniBtn testId={`pogodex-save-${b.id}`} title={marked ? "Remove from POG'KET WALLET" : "Save ♥ to my POG'KET WALLET"} label="Save" tone={marked ? "bg-rose-500/25 border-rose-400/70" : "bg-slate-800/70 border-slate-600/60"} onClick={() => onToggleMark(b)}>
          <Heart className={`w-4 h-4 ${marked ? "text-rose-300 fill-rose-400" : "text-slate-400"}`} />
        </MiniBtn>
        <MiniBtn testId={`pogodex-route-${b.id}`} title="POG'N GO — route there" label="Pog" tone="bg-emerald-500/15 border-emerald-400/40" onClick={() => onRoute(b)}>🎯</MiniBtn>
        <MiniBtn testId={`pogodex-flics-${b.id}`} title="PICS & FLICS 🔥 — see this spot's deal pics & videos" label="Flic" tone="bg-amber-500/15 border-amber-400/50" onClick={() => window.location.assign(`/flics?biz=${encodeURIComponent(b.id)}&bizname=${encodeURIComponent(b.name || "")}&blat=${b.lat ?? ""}&blng=${b.lng ?? ""}`)}>
          <Film className="w-4 h-4 text-amber-300" />
        </MiniBtn>
      </span>
    </div>
  );
};

// 📖 POG'ODEX — every merchant on the radar, auto-indexed into its category.
// Tap a category to browse, or search to jump straight to a spot.
export const PogodexSheet = ({ open, onClose, businesses, categories, driver, hot = [], onMerchant, onRoute, onFilterCategory, activeCategory, openLink }) => {
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});
  const [dealsOnly, setDealsOnly] = useState(false);
  const [marks, setMarks] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("airpog_pogodex_marks") || "[]")); } catch { return new Set(); }
  });
  const query = q.trim().toLowerCase();

  const toggleMark = (b) => {
    const id = typeof b === "string" ? b : b.id;
    const n = new Set(marks);
    const adding = !n.has(id);
    if (adding) n.add(id); else n.delete(id);
    const arr = [...n].slice(-100);
    try { localStorage.setItem("airpog_pogodex_marks", JSON.stringify(arr)); } catch { /* ignore */ }
    window.dispatchEvent(new Event("airpog:marks-changed"));
    feedCapacitor();
    if (driver) {
      axios.put(`${API}/consumer/pogmarks`, { marks: arr }, { withCredentials: true }).catch(() => {});
      // ♥ HARMONY — mirror the save into the POG'KET WALLET with app/web links attached
      const links = (typeof b === "object" && b.links) || {};
      const appLink = links.ios_app || links.android_app || "";
      const web = links.website || links.pog_link || links.order_url || "";
      const post = (meta) => axios.post(`${API}/consumer/favorites/${encodeURIComponent(id)}`, meta, { withCredentials: true }).catch(() => {});
      const near = (a, z) => { a = (a || "").toLowerCase().replace(/[^a-z0-9]/g, ""); z = (z || "").toLowerCase().replace(/[^a-z0-9]/g, ""); return a && z && (a.includes(z) || z.includes(a)); };
      const finderHit = typeof b === "object" ? appMap[(b.name || "").trim().toLowerCase()] : null;
      if (adding && typeof b === "object" && !appLink && finderHit && finderHit.app_url) {
        post({ name: b.name || "", web: web || finderHit.web || "", app_url: finderHit.app_url, logo: finderHit.logo || "" });
      } else if (adding && typeof b === "object" && !appLink) {
        // 🔎 enrich via MERCHANT FINDER — grab their app/logo if the map record lacks links
        axios.get(`${API}/merchant-finder`, { params: { q: b.name || "" } }).then((r) => {
          const hit = (r.data.results || []).find((s) => near(s.name, b.name));
          post({ name: b.name || "", web: web || hit?.web || "", app_url: hit?.has_app ? hit.app_url : "", logo: hit?.logo || "" });
        }).catch(() => post({ name: b.name || "", web, app_url: "", logo: "" }));
      } else {
        post(adding && typeof b === "object" ? { name: b.name || "", web, app_url: appLink, logo: "" } : {});
      }
    }
    setMarks(n);
  };

  // 📤 Share a merchant — native share sheet, clipboard fallback with ✓ feedback.
  const [sharedId, setSharedId] = useState(null);
  const [rewardMsg, setRewardMsg] = useState(null);
  const [topApps, setTopApps] = useState([]);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/pogapps/top`).then((r) => setTopApps(r.data.top || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (open && activeCategory) setExpanded((e) => ({ ...e, [activeCategory]: true }));
  }, [open, activeCategory]);
  const sharedT = useRef(null);
  const rewardT = useRef(null);
  useEffect(() => () => { clearTimeout(sharedT.current); clearTimeout(rewardT.current); }, []);
  const claimShareReward = () => {
    if (!driver) return;
    axios.post(`${API}/consumer/share-reward`, {}, { withCredentials: true }).then((r) => {
      if (r.data.awarded > 0) {
        setRewardMsg(`+${r.data.awarded} POG BUCKS for sharing! 🎉`);
        clearTimeout(rewardT.current);
        rewardT.current = setTimeout(() => setRewardMsg(null), 3500);
      }
    }).catch(() => {});
  };
  const shareMerchant = async (b) => {
    const url = `${window.location.origin}/api/share/${encodeURIComponent(b.id)}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "AIR.POG", text: `🔥 ${b.name} is on AIR.POG — find it near you:`, url });
      } else {
        await navigator.clipboard.writeText(url);
        setSharedId(b.id);
        clearTimeout(sharedT.current);
        sharedT.current = setTimeout(() => setSharedId(null), 2000);
      }
      claimShareReward();
    } catch { /* user cancelled */ }
  };

  // ☁️ Cross-device sync: merge server POGMARKS with this device's on first open.
  const syncedRef = useRef(false);
  useEffect(() => {
    if (!open || !driver || syncedRef.current) return;
    syncedRef.current = true;
    axios.get(`${API}/consumer/pogmarks`, { withCredentials: true }).then((r) => {
      const server = [...(r.data.marks || []), ...((driver && driver.favorites) || [])];
      setMarks((local) => {
        const union = new Set([...server, ...local]);
        const arr = [...union].slice(-100);
        try { localStorage.setItem("airpog_pogodex_marks", JSON.stringify(arr)); } catch { /* ignore */ }
        if (arr.length !== server.length) axios.put(`${API}/consumer/pogmarks`, { marks: arr }, { withCredentials: true }).catch(() => {});
        return union;
      });
    }).catch(() => {});
  }, [open, driver]);

  // 📖 Merchant analytics: count POG'ODEX opens of a listing.
  const openMerchant = (b) => {
    axios.post(`${API}/pogodex/${encodeURIComponent(b.id)}/view`).catch(() => {});
    onMerchant(b);
  };

  const catIds = useMemo(() => new Set((categories || []).map((c) => c.id)), [categories]);

  const groups = useMemo(() => {
    const m = new Map();
    const marked = [];
    for (const b of businesses || []) {
      if (dealsOnly && !(b.has_deals || b.featured)) continue;
      const id = b.is_sale ? "personal_sale" : (b.category_id || "other");
      const label = b.is_sale ? "Personal POGs" : (b.category_label || "Other");
      if (query && !(`${b.name} ${label} ${b.featured_deal || ""}`.toLowerCase().includes(query))) continue;
      const g = m.get(id) || { id, label, icon: b.is_sale ? "Tag" : b.icon, items: [], deals: 0 };
      g.items.push(b);
      if (b.has_deals || b.featured) g.deals += 1;
      m.set(id, g);
      if (marks.has(b.id)) marked.push(b);
    }
    const byRank = (a, b) => (marks.has(b.id) - marks.has(a.id)) || (b.featured - a.featured) || (b.has_deals - a.has_deals) || (a.distance_miles ?? 99) - (b.distance_miles ?? 99);
    const out = [...m.values()].filter((g) => g.items.length);
    out.forEach((g) => g.items.sort(byRank));
    out.sort((a, b) => (b.deals - a.deals) || (b.items.length - a.items.length));
    if (hot.length && !query && !dealsOnly) {
      const byId = new Map((businesses || []).map((b) => [b.id, b]));
      const hotItems = hot.map((h) => byId.get(h.business_id)).filter(Boolean);
      if (hotItems.length) out.unshift({ id: "__hot", label: "HOT THIS WEEK", icon: null, items: hotItems, deals: hotItems.filter((x) => x.has_deals || x.featured).length });
    }
    if (marked.length) {
      marked.sort(byRank);
      out.unshift({ id: "__marks", label: "MY POG'KET SAVES ♥", icon: null, items: marked, deals: marked.filter((x) => x.has_deals || x.featured).length });
    }
    if (activeCategory) {
      const i = out.findIndex((g) => g.id === activeCategory);
      if (i > 0) out.unshift(out.splice(i, 1)[0]);
    }
    return out;
  }, [businesses, query, dealsOnly, marks, hot, activeCategory]);

  const total = useMemo(() => groups.reduce((n, g) => n + (g.id.startsWith("__") ? 0 : g.items.length), 0), [groups]);
  const isOpen = (id) => id.startsWith("__") || !!query || !!expanded[id];

  // 🔥 APP LIGHTER — batch-lookup official apps for merchants missing app links (server-cached 7d)
  const [appMap, setAppMap] = useState({});
  const appReqRef = useRef(new Set());
  useEffect(() => {
    if (!open) return;
    const want = [];
    for (const g of groups) {
      if (!isOpen(g.id)) continue;
      for (const b of g.items) {
        const l = b.links || {};
        const nm = (b.name || "").trim();
        if (!l.ios_app && !l.android_app && nm.length >= 2 && !appReqRef.current.has(nm.toLowerCase())) {
          appReqRef.current.add(nm.toLowerCase());
          want.push(nm);
        }
        if (want.length >= 40) break;
      }
      if (want.length >= 40) break;
    }
    if (!want.length) return;
    axios.post(`${API}/merchant-finder/apps`, { names: want })
      .then((r) => setAppMap((m) => ({ ...m, ...(r.data.apps || {}) })))
      .catch(() => {});
  }, [open, groups, expanded, query]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[2100] bg-black/60 flex items-end sm:items-center justify-center"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
        >
          <motion.div
            data-testid="pogodex-sheet"
            initial={{ opacity: 0, y: 80 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 80 }}
            transition={{ type: "spring", damping: 28, stiffness: 320 }}
            onClick={(e) => e.stopPropagation()}
            className="glass-strong w-full sm:max-w-lg h-[86vh] sm:h-[80vh] rounded-t-3xl sm:rounded-3xl border border-amber-400/25 flex flex-col overflow-hidden"
          >
            <div className="px-4 pt-4 pb-3 border-b border-slate-700/60 shrink-0">
              <div className="flex items-center justify-between">
                <p className="font-heading font-black text-lg text-white flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-amber-400" /> POG'ODEX
                  <span data-testid="pogodex-total" className="text-[10px] font-black text-amber-300 bg-amber-500/15 border border-amber-400/40 rounded-full px-2 py-0.5">{total} indexed</span>
                </p>
                <button data-testid="pogodex-close" onClick={onClose} className="w-9 h-9 rounded-xl bg-slate-800/70 hover:bg-slate-700 flex items-center justify-center">
                  <X className="w-5 h-5 text-slate-300" />
                </button>
              </div>
              <p className="text-[11px] text-slate-400 font-semibold mt-0.5">Every merchant on your radar, indexed by category.</p>
              <button data-testid="pogodex-jobs-btn" onClick={() => { onClose && onClose(); window.location.assign("/jobs"); }}
                className="mt-2 w-full h-9 rounded-xl bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 font-heading font-black text-[11px] tracking-widest flex items-center justify-center gap-1.5 hover:bg-emerald-500/25 transition-colors">
                💼 JOBS RADAR — local openings, FREE →
              </button>
              <button data-testid="pogodex-wallet-btn" onClick={() => { onClose && onClose(); window.location.assign("/hq"); }}
                className="mt-1.5 w-full h-9 rounded-xl bg-rose-500/15 border border-rose-400/40 text-rose-300 font-heading font-black text-[11px] tracking-widest flex items-center justify-center gap-1.5 hover:bg-rose-500/25 transition-colors">
                ♥ POG'KET WALLET — your saved merchants, FREE →
              </button>
              {topApps.length > 0 && (
                <div className="mt-2" data-testid="pogodex-top-apps">
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">🔥 TOP POG APPS</p>
                  <div className="flex flex-wrap gap-1">
                    {topApps.map((t, i) => (
                      <button key={t.id} data-testid={`pogodex-top-app-${t.id}`}
                        onClick={() => { onClose && onClose(); window.location.assign(`/pogapp/${t.id}`); }}
                        className="text-[10px] font-black bg-slate-800/80 border border-fuchsia-400/30 text-fuchsia-200 rounded-full px-2.5 py-1 active:scale-95 transition-transform">
                        {i === 0 ? "👑 " : "📱 "}{t.name} · {t.saves}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-2.5 relative">
                <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="pogodex-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search merchants, categories, deals…"
                  className="w-full h-11 rounded-2xl bg-slate-900/80 border border-slate-700 focus:border-amber-400/60 outline-none pl-9 pr-3 text-sm font-semibold text-white placeholder:text-slate-500"
                />
              </div>
              <div className="mt-2 flex items-center gap-2">
                <button
                  data-testid="pogodex-deals-toggle"
                  onClick={() => setDealsOnly((v) => !v)}
                  className={`h-8 px-3 rounded-full text-[11px] font-heading font-extrabold border transition-colors ${dealsOnly ? "bg-amber-500/25 border-amber-400/70 text-amber-200" : "bg-slate-900/70 border-slate-700 text-slate-300 hover:border-amber-400/40"}`}
                >
                  🔥 Deals only
                </button>
                {rewardMsg && (
                  <span data-testid="pogodex-share-reward" className="text-[10px] font-black text-emerald-300 bg-emerald-500/15 border border-emerald-400/40 rounded-full px-2 py-0.5">{rewardMsg}</span>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-2.5 py-2 no-scrollbar">
              {groups.length === 0 && (
                <p data-testid="pogodex-empty" className="text-center text-sm font-bold text-slate-400 mt-10">
                  {query ? "No merchants match that — try another word! 🔍" : dealsOnly ? "No live deals on the radar right now — check back soon! 🔥" : "Nothing on the radar yet — widen your radius! 📡"}
                </p>
              )}
              {groups.map((g) => (
                <div key={g.id} className="mb-1">
                  <div
                    data-testid={`pogodex-category-${g.id}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpanded((e) => ({ ...e, [g.id]: !e[g.id] }))}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpanded((x) => ({ ...x, [g.id]: !x[g.id] })); } }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-2xl bg-slate-900/60 hover:bg-slate-800/70 border border-slate-700/50 transition-colors cursor-pointer"
                  >
                    <span className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 border ${g.id === "__marks" ? "bg-rose-500/15 border-rose-400/40" : g.id === "__hot" ? "bg-orange-500/15 border-orange-400/40" : "bg-sky-500/15 border-sky-400/30"}`}>
                      {g.id === "__marks"
                        ? <Heart className="w-4 h-4 text-rose-300 fill-rose-400" />
                        : g.id === "__hot"
                          ? <Flame className="w-4 h-4 text-orange-300 fill-orange-400" />
                          : createElement(getIcon(g.icon), { className: "w-4 h-4 text-sky-300" })}
                    </span>
                    <span className="font-heading font-extrabold text-sm text-white flex-1 text-left truncate">{g.label}</span>
                    {g.id === activeCategory && (
                      <span data-testid="pogodex-onmap-badge" className="text-[9px] font-black text-emerald-300 bg-emerald-500/15 border border-emerald-400/40 rounded-full px-1.5 py-0.5 shrink-0">📍 ON MAP</span>
                    )}
                    {g.deals > 0 && (
                      <span className="text-[10px] font-black text-amber-300 bg-amber-500/15 border border-amber-400/40 rounded-full px-2 py-0.5 shrink-0">🔥 {g.deals} deal{g.deals > 1 ? "s" : ""}</span>
                    )}
                    <span className="text-[11px] font-bold text-slate-400 shrink-0">{g.items.length}</span>
                    {catIds.has(g.id) && (
                      <button
                        data-testid={`pogodex-map-${g.id}`}
                        title={g.id === activeCategory ? "Clear map filter" : `Show only ${g.label} on the map`}
                        onClick={(e) => { e.stopPropagation(); onFilterCategory?.(g.id === activeCategory ? null : g.id); }}
                        className={`w-8 h-8 rounded-xl border flex items-center justify-center shrink-0 active:scale-90 transition-transform ${g.id === activeCategory ? "bg-emerald-500/80 border-emerald-300" : "bg-emerald-500/15 border-emerald-400/40"}`}
                      >
                        <MapPin className={`w-4 h-4 ${g.id === activeCategory ? "text-white" : "text-emerald-300"}`} />
                      </button>
                    )}
                    <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${isOpen(g.id) ? "rotate-180" : ""}`} />
                  </div>
                  <AnimatePresence initial={false}>
                    {isOpen(g.id) && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div className="pt-0.5 pb-1">
                          {g.items.map((b) => (
                            <MerchantRow key={b.id} b={b} marked={marks.has(b.id)} shared={sharedId === b.id} onToggleMark={toggleMark} onShare={shareMerchant} onMerchant={openMerchant} onRoute={onRoute} openLink={openLink} appMap={appMap} />
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
